// boosters.js
// Capa de ayudas / rewards del Zodiac Crush.
// NO modifica reglas base del engine.
// SOLO analiza el board y devuelve acciones sugeridas.
//
// Engine existente usado:
// - swap.js → para detectar jugadas válidas
// - rng.js  → para shuffle futuro

import { listAdjacentPairs, isHintSwapValid, hasAnyValidMove } from './swap';
import { cloneBoard } from './board';

/**
 * 🧭 HINT / PISTA
 * Devuelve una jugada válida para sugerir al usuario.
 *
 * @param {Object} board - board actual del engine
 * @returns {null | { a: {r,c}, b: {r,c} }}
 */
export function getHint(board) {
  if (!board) return null;

  try {
    const pairs = listAdjacentPairs(board);

    for (const [a, b] of pairs) {
      if (isHintSwapValid(board, a, b)) {
        return {
          a: { r: a.r, c: a.c },
          b: { r: b.r, c: b.c },
        };
      }
    }

    return null;
  } catch (e) {
    return null;
  }
}

/**
 * 🔍 Detecta si el tablero tiene al menos una jugada válida.
 * Wrapper del engine para uso desde UI / boosters.
 *
 * @param {Object} board
 * @returns {boolean}
 */
export function findAnyValidMove(board) {
  if (!board) return false;
  try {
    return hasAnyValidMove(board);
  } catch (e) {
    return false;
  }
}

/**
 * 🔀 SHUFFLE (preparado para el futuro)
 * Reordena el tablero sin cambiar cantidad de tiles.
 * NO se usa todavía, pero queda listo.
 *
 * @param {Object} board
 * @param {Function} rng - random function del engine
 * @returns {Object} newBoard
 */
export function shuffleBoard(board, rng = Math.random) {
  if (!board) return board;

  const b = cloneBoard(board);

  const tiles = [];
  for (let r = 0; r < b.rows; r++) {
    for (let c = 0; c < b.cols; c++) {
      if (b.grid[r][c]) tiles.push(b.grid[r][c]);
    }
  }

  // Fisher–Yates shuffle
  for (let i = tiles.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
  }

  let idx = 0;
  for (let r = 0; r < b.rows; r++) {
    for (let c = 0; c < b.cols; c++) {
      b.grid[r][c] = tiles[idx++];
    }
  }

  return b;
}

/**
 * Helper: transforma un hint en celdas para render UI
 * BoardView no debe conocer lógica del engine.
 */
export function hintToCells(hint) {
  if (!hint) return [];
  return [
    { r: hint.from.r, c: hint.from.c },
    { r: hint.to.r, c: hint.to.c },
  ];
}

/**
 * 💣 ROW BOMB (bomba de fila)
 * Devuelve las celdas que serían afectadas al explotar una fila.
 * El UI puede usar esto para animación/highlight.
 *
 * @param {Object} board
 * @param {number} rowIndex
 * @returns {Array<{r:number,c:number}>}
 */
export function getRowBombCells(board, rowIndex) {
  if (!board) return [];
  const r = Math.max(0, Math.min(Number(rowIndex) || 0, (board.rows || 0) - 1));
  const cols = Number(board.cols || 0);
  if (!Number.isFinite(r) || cols <= 0) return [];

  const cells = [];
  for (let c = 0; c < cols; c++) {
    cells.push({ r, c });
  }
  return cells;
}

/**
 * Helper: dado un tap en celda, devuelve el rowIndex a explotar.
 *
 * @param {{r:number,c:number}} cell
 * @returns {number|null}
 */
export function cellToRowBombRow(cell) {
  if (!cell || typeof cell !== 'object') return null;
  const r = Number(cell.r);
  return Number.isFinite(r) ? r : null;
}

/**
 * 💣 ROW BOMB (aplicar)
 * Clona el board y vacía (setea null) todas las tiles de una fila.
 * OJO: esto NO aplica gravedad/refill. Eso debe hacerlo el engine (resolve / drop) luego.
 *
 * @param {Object} board
 * @param {number} rowIndex
 * @returns {Object} newBoard
 */
export function applyRowBomb(board, rowIndex) {
  if (!board) return board;

  const b = cloneBoard(board);
  const r = Math.max(0, Math.min(Number(rowIndex) || 0, (b.rows || 0) - 1));
  const cols = Number(b.cols || 0);
  if (cols <= 0) return b;

  for (let c = 0; c < cols; c++) {
    b.grid[r][c] = null;
  }

  return b;
}
